import type { DifferentProps } from '@/components/claims/Different';
import type { HeroProps } from '@/components/claims/Hero';
import type { HowItWorksProps } from '@/components/claims/HowItWorks';
import type { ReviewsProps } from '@/components/claims/Reviews';
import type { StatsProps } from '@/components/claims/Stats';
import { sharedConfig } from '@/pageConfig/shared';
import approvalImg from '@/static/claims/lemonade-claims-approval-process.png';
import aiImg from '@/static/claims/lemonade-claims-chatbot-ai.png';
import chatImg from '@/static/claims/lemonade-claims-chatbot.png';
import buttonImg from '@/static/claims/lemonade-claims-lemonade-app.png';
import ogImage from '@/static/claims/og.png';
import appStoreImage from '@/static/claims/review-app-store.png';
import highyaImage from '@/static/claims/review-highya.png';
import supermoneyImage from '@/static/claims/review-supermoney.png';
import thejaImage from '@/static/claims/review-theja.png';
import timothyImage from '@/static/claims/review-timothy.png';
import twitterImage from '@/static/claims/review-twitter.png';
import type { BasePageConfig } from '../../BasePageConfig';
export interface Config extends BasePageConfig {
  hero: HeroProps;
  stats: StatsProps;
  howItWorks: HowItWorksProps;
  reviews: ReviewsProps;
  different: DifferentProps;
}

const productJsonld = {
  '@context': 'https://schema.org/',
  '@type': 'Product',
  name: 'Lemonade Claims',
  description: 'Insurance claims for renters and homeowners',
  brand: {
    '@type': 'Brand',
    name: 'Lemonade',
    logo: 'https://www.lemonade.com/assets/logo-e0f7c4f109ef5af2bb9604137856587ca61a56ed8c3ab2b8f56fcec923babafc.svg',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.75',
    ratingCount: '20',
  },
};

const webpageJsonld = {
  '@context': 'https://schema.org/',
  '@type': 'WebPage',
  name: 'Lemonade Claims',
  image:
    'https://www.lemonade.com/assets/claims-og-889dc73fa6fdcef7db287d2ef89dbeeb9ec681503a24803859d68f1a86065ff2.png',
  description:
    'Easily submit claims through the Lemonade app. They will either be processed instantly by a bot or personally through our claims team.',
};

export const config: Config = {
  layout: { ...sharedConfig['en-US'].layout },
  seo: {
    title: "How Lemonade's Tech-Powered Claims Work | Lemonade Insurance",
    metaTitle: "How Lemonade's Tech-Powered Claims Work | Lemonade Insurance",
    description:
      'Lemonade handles claims quickly and hassle-free, thanks to our powerful tech and devoted claims team. Easily submit claims through the Lemonade app and your claim will either be processed instantly by a bot or personally by Lemonade’s dedicated claims team.',
    ogImage: ogImage.src,
    jsonld: [productJsonld, webpageJsonld],
  },
  hero: {
    title: "The Secret Behind Lemonade's Claims",
    subtitle:
      'Claims are handled by our AI and devoted team. File a claim quickly and hassle-free with the Lemonade app.',
  },
  stats: {
    steps: [
      {
        title: '3sec',
        subtitle: 'Our fastest recorded claim',
      },
      {
        title: '+4.9',
        subtitle: 'claims rating on our app',
        showRatingStars: true,
        order: [3, 3, 2],
        mt: [65, 65, 0],
      },
      {
        order: [2, 2, 3],
        title: '~40%',
        subtitle: 'of claims handled instantly',
      },
    ],
  },
  howItWorks: {
    title: 'How Lemonade Claims Work',
    subtitle: 'Seamlessly file claims from your phone - anytime, anywhere',
    steps: {
      claim_button: {
        arrowTop: ['220px', '230px', '69px'],
        image: buttonImg,
        content: "Tap the '<b>File a Claim</b>' button in the Lemonade app",
      },
      what_happened: {
        arrowTop: ['210px', '230px', '69px'],
        image: chatImg,
        content: 'Tell us what happened',
      },
      anti_fraud: {
        arrowTop: ['220px', '220px', '13px'],
        image: aiImg,
        content: 'Our AI runs dozens of anti-fraud algorithms',
      },
      instantly_approved: {
        image: approvalImg,
        content: 'If your claim is <b>instantly</b> approved, our AI will pay it in seconds',
      },
      humans: {
        image: approvalImg,
        content: 'Otherwise, our AI hands over your claim to our devoted team of <b>humans</b> to handle ASAP',
      },
    },
  },
  reviews: {
    title: 'Lemonade Claims Reviews',
    reviews: [
      {
        source: {
          image: twitterImage.src,
          width: 22,
          height: 17,
        },
        image: thejaImage.src,
        name: 'Theja',
        handle: '@swarup_theja',
        review: `I claimed on 4th which is a holiday and <span>@Lemonade_Inc</span> got back this morning at 6AM. Still can't believe this`,
        showRating: true,
      },
      {
        source: {
          image: twitterImage.src,
          width: 22,
          height: 17,
        },
        image: timothyImage.src,
        name: 'Timothy Miko',
        handle: '@timothymiko',
        review: `Working with <span>@Lemonade_Inc</span> after my bikes were stolen has been an awesome experience. Never thought I'd say that about an insurance product.`,
        showRating: true,
      },
      {
        source: {
          image: highyaImage.src,
          width: 29,
          height: 15,
        },
        name: 'Nancy B.',
        rating: 5,
        review:
          'A pipe burst, and I had to figure out what to do with the holidays approaching and no kitchen table to sit at. I sent everything to Lemonade, and they gave me the money to replace the dining room table in less than a week.',
        showRating: true,
      },
      {
        source: {
          image: supermoneyImage.src,
          width: 22,
          height: 22,
        },
        name: 'Kelly B.',
        rating: 5,
        review:
          'Lemonade went above and beyond to approve a claim I made only 2 weeks after signing up for their renters insurance. This company did the kindest gesture to help me out and managed to cover my losses on the same day.',
        showRating: true,
      },
      {
        source: {
          image: appStoreImage.src,
          width: 88,
          height: 23,
        },
        name: 'Narin S.',
        rating: 5,
        review:
          'Lemonade is insurance for humans. The experience is amazing both from a UX perspective and the empathy and full support of the claims team. Best thing to happen to the insurance industry.',
        showRating: true,
      },
      {
        source: {
          image: supermoneyImage.src,
          width: 22,
          height: 22,
        },
        name: 'Laura R.',
        rating: 5,
        review: `Today, my husband's iPhone was stolen. Opened the app, spent about 2 mins filling out the claim and recorded a video statement, and literally 10 seconds later, my claim was approved. These guys have a customer for life.`,
        showRating: true,
      },
    ],
  },
  different: {
    title: 'How Lemonade Claims Are Different',
    subtitle: `Unlike any other insurance company, we take a flat fee from your premium, use the rest to pay claims, and <a href="/nl/en/giveback">give back</a> what’s left to causes you care about. We gain nothing by delaying or denying claims, so we handle them <a href="/nl/en/faq#claims">quickly and fairly</a>`,
    cta: {
      label: 'Check Our Prices',
    },
  },
};
